import React, { ReactNode, useContext } from 'react'
import { IntlProvider as Intl } from 'react-intl'
import { datadogLogs } from '@datadog/browser-logs'
import staticCopy from 'intl/copy'
import { AppStateContext } from 'containers/State/AppState'
import { ThemeProvider } from '@focaldata/cin-ui-components'
import { ResolvedIntlConfig } from 'react-intl/src/types'
import { getDirection } from './IntlUtils'

const FALLBACK_LANGUAGE = 'en'
const FALLBACK_COUNTRY = 'GB'

const copy = staticCopy as Record<
  string,
  ResolvedIntlConfig['messages'] | undefined
>

const IntlProvider = ({ children }: { children: ReactNode }) => {
  const {
    renderedQuestionnaire: [questionnaire],
    selectedLanguage: [selectedLanguage]
  } = useContext(AppStateContext)

  const language =
    selectedLanguage ??
    questionnaire?.questionnaire.locale.language ??
    FALLBACK_LANGUAGE
  const country =
    questionnaire?.questionnaire.locale.country ?? FALLBACK_COUNTRY

  const direction = getDirection(language)
  const fullLocale = `${language}-${country}`

  let messages = copy[fullLocale] ?? copy[language]
  let effectiveLocale = fullLocale

  if (!messages) {
    datadogLogs.logger.warn(
      'No UI copy for selected locale; falling back to English',
      { fullLocale, language, country }
    )
    messages = copy[FALLBACK_LANGUAGE]
    effectiveLocale = FALLBACK_LANGUAGE
  }

  if (!messages) {
    throw new Error(`Cannot render survey for ${fullLocale}`)
  }

  return (
    <ThemeProvider direction={direction}>
      <Intl locale={effectiveLocale} messages={messages}>
        {children}
      </Intl>
    </ThemeProvider>
  )
}

export default IntlProvider
